/**
 * O que faz: Página de cadastro de novos usuários do Gens CRM.
 * Onde usa: Rota pública /signup.
 * Conexões: AuthContext (signUp), React Router e TelaLoginLayout (mesma identidade da tela de login).
 * Tabelas: Nenhuma direta; o Supabase Auth cria o usuário e o trigger do banco cria um
 *          perfil sem acesso ao CRM. Após confirmar o email e entrar, o usuário escolhe
 *          um plano e conclui o primeiro pagamento.
 * Data: 24/09/2026 (visual alinhado ao login).
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { TelaLoginLayout } from '@/components/auth/TelaLoginLayout';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { SimboloGens } from "@/components/brand/MarcaGens";

export const SignUpPage = () => {
  const {
    signUp,
    loading
  } = useAuth();
  const navigate = useNavigate();

  // Estados do formulário
  const [formData, setFormData] = useState({
    nomeCompleto: '',
    nomeClinica: '',
    email: '',
    password: '',
    confirmPassword: '',
    aceitaPolitica: false
  });

  // Estados de UI
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Atualizar campos do formulário
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro do campo quando usuário começa a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validar email
  const isValidEmail = (email: string) => {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };

  // Validar força da senha
  const isStrongPassword = (password: string) => {
    return password.length >= 6;
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.nomeClinica.trim()) {
      newErrors.nomeClinica = 'Nome da clínica é obrigatório';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Email deve ter formato válido';
    }

    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (!isStrongPassword(formData.password)) {
      newErrors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirmação de senha é obrigatória';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Senhas não coincidem';
    }

    if (!formData.aceitaPolitica) {
      newErrors.aceitaPolitica = 'É necessário aceitar a Política de Privacidade';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Classe do input: mesma do login, com borda vermelha quando o campo tem erro.
  const classeCampo = (campo: string) =>
    `login-gens-input ring-0 focus-visible:ring-0 ${errors[campo] ? 'login-gens-input-erro' : ''}`;

  // Submeter formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Verifique os campos do formulário');
      return;
    }
    try {
      await signUp(
        formData.email.trim(),
        formData.password,
        formData.nomeCompleto.trim() || undefined,
        formData.nomeClinica.trim(),
        formData.aceitaPolitica
      );

      // Redirecionar para login após cadastro bem-sucedido
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      // Erro já é tratado no contexto de autenticação
      console.error('Erro no cadastro:', error);
    }
  };

  // Mesma estrutura visual da tela de login (TelaLoginLayout + classes login-gens-*)
  // para o cadastro parecer parte do mesmo fluxo de entrada no Gens CRM.
  return (
    <TelaLoginLayout>
      <Link to="/" className="login-gens-brand" aria-label="Gens CRM — início">
        <span aria-hidden="true"><SimboloGens className="h-[58%] w-auto" /></span>
        gens
        <span className="login-gens-brand-suffix">CRM</span>
      </Link>

      <h1 className="login-gens-title">
        Crie sua conta no
        <span>Gens CRM</span>
      </h1>
      <p className="login-gens-lead">
        Crie sua conta e escolha um plano para liberar o Gens CRM.
      </p>

      <form onSubmit={handleSubmit} className="login-gens-acoes" noValidate>
        <div className="login-gens-campo">
          <label htmlFor="nomeCompleto">Nome completo (opcional)</label>
          <Input
            id="nomeCompleto"
            name="nomeCompleto"
            type="text"
            value={formData.nomeCompleto}
            onChange={handleInputChange}
            placeholder="Seu nome"
            className={classeCampo('nomeCompleto')}
            autoComplete="name"
          />
          {errors.nomeCompleto && <p className="login-gens-erro">{errors.nomeCompleto}</p>}
        </div>

        <div className="login-gens-campo">
          <label htmlFor="nomeClinica">Nome da clínica *</label>
          <Input
            id="nomeClinica"
            name="nomeClinica"
            type="text"
            value={formData.nomeClinica}
            onChange={handleInputChange}
            placeholder="Nome da sua clínica"
            className={classeCampo('nomeClinica')}
            required
            autoComplete="organization"
          />
          {errors.nomeClinica && <p className="login-gens-erro">{errors.nomeClinica}</p>}
        </div>

        <div className="login-gens-campo">
          <label htmlFor="email">Email *</label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="voce@clinica.com"
            className={classeCampo('email')}
            required
            autoComplete="email"
          />
          {errors.email && <p className="login-gens-erro">{errors.email}</p>}
        </div>

        <div className="login-gens-campo">
          <label htmlFor="password">Senha *</label>
          <div className="login-gens-senha">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Crie uma senha"
              className={classeCampo('password')}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="login-gens-olho"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {/* A dica some quando há erro, para não empilhar duas mensagens no mesmo campo. */}
          {errors.password
            ? <p className="login-gens-erro">{errors.password}</p>
            : <p className="login-gens-dica">Mínimo de 6 caracteres</p>}
        </div>

        <div className="login-gens-campo">
          <label htmlFor="confirmPassword">Confirmar senha *</label>
          <div className="login-gens-senha">
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Repita a senha"
              className={classeCampo('confirmPassword')}
              required
              autoComplete="new-password"
            />
            <button
              type="button"
              className="login-gens-olho"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirmPassword && <p className="login-gens-erro">{errors.confirmPassword}</p>}
        </div>

        <div>
          <label htmlFor="aceitaPolitica" className="login-gens-aceite">
            <input
              id="aceitaPolitica"
              name="aceitaPolitica"
              type="checkbox"
              checked={formData.aceitaPolitica}
              onChange={handleInputChange}
            />
            <span>
              Aceito a{' '}
              <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
                Política de Privacidade
              </a>{' '}
              *
            </span>
          </label>
          {errors.aceitaPolitica && <p className="login-gens-erro">{errors.aceitaPolitica}</p>}
        </div>

        <button type="submit" className="login-gens-enviar" disabled={loading}>
          {loading ? 'Criando conta...' : 'Criar conta e escolher plano'}
        </button>
      </form>

      <p className="login-gens-criar">
        Já tem uma conta?{' '}
        <Link to="/login">Fazer login</Link>
      </p>

      <Link to="/" className="login-gens-voltar">
        <ArrowLeft className="w-4 h-4" />
        Voltar ao início
      </Link>
    </TelaLoginLayout>
  );
};
export default SignUpPage;
